import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { acceptInvite } from "./api";

const InviteAcceptPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("processing");
  const [errorMsg, setErrorMsg] = useState(null);

  const mutation = useMutation({
    mutationFn: (t) => acceptInvite(t),
    onSuccess: () => {
      setStatus("success");
      setTimeout(() => navigate("/overview"), 2000);
    },
    onError: (err) => {
      setStatus("error");
      const code = err?.body?.error?.code;
      if (code === "INVITE_INVALID_OR_EXPIRED") {
        setErrorMsg("This invite link is invalid or has expired. Please contact your administrator for a new invite.");
      } else if (code === "EMAIL_MISMATCH") {
        setErrorMsg("The email on your Keycloak account does not match the invited email address. Please sign in with the correct account.");
      } else {
        setErrorMsg(err?.body?.error?.message || "Something went wrong accepting the invite.");
      }
    },
  });

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("No invite token found in the URL.");
      return;
    }
    mutation.mutate(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-2">
      <div className="bg-card rounded-card border border-card-bd p-8 max-w-md w-full mx-4 text-center">
        {status === "processing" && (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-text mb-2">Accepting invite...</h2>
            <p className="text-sm text-text-2">Setting up your account. This will only take a moment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="h-10 w-10 rounded-full bg-success-soft flex items-center justify-center mx-auto mb-4">
              <svg className="h-6 w-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-text mb-2">Welcome!</h2>
            <p className="text-sm text-text-2">Your account is ready. Redirecting you now...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="h-10 w-10 rounded-full bg-danger-soft flex items-center justify-center mx-auto mb-4">
              <svg className="h-6 w-6 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-text mb-2">Invite failed</h2>
            <p className="text-sm text-text-2 mb-4">{errorMsg}</p>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 bg-primary text-white rounded-ctrl hover:bg-primary-hover text-sm"
            >
              Go to home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
